/**
 * ImpersonationPicker — admin-only "View as…" header control.
 *
 * Visible to admin / super_admin, hidden when an impersonation is
 * already active (the banner provides the path out).
 *
 * Has its own local "test accounts only" filter — defaults to ON
 * since impersonating test users is the primary use case. Independent
 * of the global Show-test-data toggle.
 */

import { useState } from 'react'
import {
  makeStyles,
  tokens,
  Button,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Input,
  Switch,
  Spinner,
  Text,
} from '@fluentui/react-components'
import { Eye20Regular, Search16Regular } from '@fluentui/react-icons'
import { useAuth } from '../context/AuthContext'
import { useImpersonation } from '../context/ImpersonationContext'
import { useImpersonationTargets } from '../hooks/useImpersonationTargets'
import { bayerColors } from '../theme'

const useStyles = makeStyles({
  surface: {
    width: '320px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  list: {
    maxHeight: '320px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    background: 'transparent',
    border: 'none',
    padding: '8px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
    fontFamily: 'inherit',
    transitionProperty: 'background',
    transitionDuration: '120ms',
    ':hover': { background: bayerColors.navyLight },
  },
  rowName: { fontSize: '13px', fontWeight: 600, color: '#242424' },
  rowMeta: { fontSize: '11px', color: '#616161' },
  testPill: {
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    background: '#e65100',
    color: '#ffffff',
    padding: '1px 4px',
    borderRadius: '3px',
    marginLeft: '4px',
  },
  empty: { fontSize: '12px', color: '#616161', padding: '12px' },
})

function isAdminRole(role?: string): boolean {
  return role === 'admin' || role === 'super_admin'
}

export function ImpersonationPicker() {
  const styles = useStyles()
  const { user } = useAuth()
  const { target, setTarget } = useImpersonation()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [testOnly, setTestOnly] = useState(true)

  const isAdmin = !!user && isAdminRole(user.role)
  const { data: targets, isLoading } = useImpersonationTargets({
    includeTest: testOnly,
    enabled: open && isAdmin && !target,
  })

  if (!isAdmin) return null
  if (target) return null // banner provides exit; hide picker while impersonating

  const filtered = (targets ?? []).filter((t) => {
    if (!search.trim()) return true
    const q = search.toLowerCase()
    return (
      t.name.toLowerCase().includes(q) ||
      t.email.toLowerCase().includes(q)
    )
  })

  return (
    <Popover open={open} onOpenChange={(_, d) => setOpen(d.open)} positioning="below-end">
      <PopoverTrigger disableButtonEnhancement>
        <Button icon={<Eye20Regular />} appearance="subtle" size="small">
          View as…
        </Button>
      </PopoverTrigger>
      <PopoverSurface>
        <div className={styles.surface}>
          <Input
            placeholder="Search by name or email…"
            value={search}
            onChange={(_, d) => setSearch(d.value)}
            contentBefore={<Search16Regular />}
            size="small"
            autoFocus
          />
          <div className={styles.filterRow}>
            <Text size={200}>Test accounts only</Text>
            <Switch
              checked={testOnly}
              onChange={(_, d) => setTestOnly(d.checked)}
            />
          </div>
          <div className={styles.list}>
            {isLoading && <Spinner size="tiny" />}
            {!isLoading && filtered.length === 0 && (
              <div className={styles.empty}>No matching users.</div>
            )}
            {filtered.map((t) => (
              <button
                key={t.id}
                type="button"
                className={styles.row}
                onClick={() => {
                  setOpen(false)
                  setTarget(t)
                }}
              >
                <span className={styles.rowName}>{t.name}</span>
                <span className={styles.rowMeta}>
                  {t.role}
                  {t.focus_area_code ? ` · ${t.focus_area_code}` : ''} · {t.email}
                </span>
              </button>
            ))}
          </div>
        </div>
      </PopoverSurface>
    </Popover>
  )
}
